import { categoryScore, criterionWeight } from './admin-evaluation-score.helpers';

export interface PdfMeta {
  module: string;
  group: string;
  year: number;
}

export interface PdfItem {
  index: string;
  title: string;
  score: number;
}

export interface PdfGroup {
  name: string;
  total: number;
  items: PdfItem[];
}

/** Nombre visible del módulo. */
export function moduleName(module: any): string {
  return module?.acronym || module?.name || '';
}

/** Etiqueta visible del grupo. */
export function groupLabel(group: any): string {
  return group?.name || String(group || '');
}

/** Nombre visible de la categoría. */
function nameOf(category: any): string {
  return category?.name || '';
}

/** Título visible del criterio. */
function titleOf(question: any): string {
  return question?.title || '';
}

/** Arma un grupo PDF con sus criterios y total. */
function buildGroup(category: any, ci: number): PdfGroup {
  return { name: nameOf(category), total: categoryScore(category), items: buildItems(category, ci) };
}

/** Arma los criterios numerados de la categoría. */
function buildItems(category: any, ci: number): PdfItem[] {
  const out: PdfItem[] = [];
  let qi = 0;
  for (const question of (category?.questions || [])) pushItem(out, ci, qi++, question);
  return out;
}

/** Agrega un criterio con sus subcriterios. */
function pushItem(out: PdfItem[], ci: number, qi: number, question: any): void {
  out.push({ index: `${ci + 1}.${qi + 1}`, title: titleOf(question), score: criterionWeight(question) });
  pushSubItems(out, ci, qi, question?.subitems);
}

/** Agrega los subcriterios numerados. */
function pushSubItems(out: PdfItem[], ci: number, qi: number, subitems: any[]): void {
  let si = 0;
  for (const sub of (subitems || [])) pushSub(out, ci, qi, si++, sub);
}

/** Agrega un subcriterio numerado. */
function pushSub(out: PdfItem[], ci: number, qi: number, si: number, sub: any): void {
  out.push({ index: `${ci + 1}.${qi + 1}.${si + 1}`, title: titleOf(sub), score: criterionWeight(sub) });
}

/** Arma los grupos PDF de todas las categorías. */
export function buildPdfGroups(categories: any[]): PdfGroup[] {
  const out: PdfGroup[] = [];
  let ci = 0;
  for (const category of (categories || [])) out.push(buildGroup(category, ci++));
  return out;
}

/** Celda de categoría fusionada por sus filas. */
function spannedCell(group: PdfGroup): any {
  return { content: group.name, rowSpan: group.items.length };
}

/** Arma la fila de un criterio con su celda de grupo. */
function itemRow(group: PdfGroup, item: PdfItem, i: number): any[] {
  if (i > 0) return ['', item.index, item.title, item.score];
  return [spannedCell(group), item.index, item.title, item.score];
}

/** Agrega las filas de criterios del grupo. */
function appendItems(out: any[][], group: PdfGroup): void {
  for (let i = 0; i < group.items.length; i++) out.push(itemRow(group, group.items[i], i));
}

/** Agrega el grupo con su fila de total. */
function appendGroup(out: any[][], group: PdfGroup): void {
  appendItems(out, group);
  out.push(['', '', `TOTAL ${group.name}`, group.total]);
}

/** Suma los totales de todos los grupos. */
export function grandTotal(groups: PdfGroup[]): number {
  let total = 0;
  for (const group of groups) total += group.total;
  return total;
}

/** Arma el cuerpo de la tabla con totales por grupo y general. */
function tableBody(groups: PdfGroup[]): any[][] {
  const out: any[][] = [];
  for (const group of groups) appendGroup(out, group);
  out.push(['', '', 'TOTAL GENERAL', grandTotal(groups)]);
  return out;
}

/** Indica si la fila es un total. */
function isTotalRow(row: any): boolean {
  return String(row.raw[2] || '').startsWith('TOTAL');
}

/** Pone en negrita las filas de totales. */
function boldTotals(data: any): void {
  if (data.section !== 'body') return;
  if (!isTotalRow(data.row)) return;
  data.cell.styles.fontStyle = 'bold';
}

/** Indica si la fila es subpregunta (índice con 3 partes). */
function isSubRow(row: any): boolean {
  return String(row.raw[1] || '').split('.').length > 2;
}

/** Quita la negrita a las subpreguntas. */
function plainSubs(data: any): void {
  if (data.section !== 'body') return;
  if (!isSubRow(data.row)) return;
  data.cell.styles.fontStyle = 'normal';
}

/** Aplica estilos de negrita por tipo de fila. */
function styleCells(data: any): void {
  boldTotals(data);
  plainSubs(data);
}

/** Indica si la celda es de categoría fusionada. */
function isSpannedCategory(data: any): boolean {
  if (data.column.index !== 0) return false;
  return !!data.cell.raw;
}

/** Dibuja el nombre de categoría en vertical. */
function paintVertical(doc: any, cell: any, text: string): void {
  doc.text(text, cell.x + 4, cell.y + cell.height - 4, { angle: 90 });
}

/** Rota las celdas de categoría a texto vertical. */
function drawVertical(data: any): void {
  if (!isSpannedCategory(data)) return;
  paintVertical(data.doc, data.cell, String(data.cell.raw));
}

/** Configuración de la tabla del formulario. */
function tableConfig(groups: PdfGroup[]): any {
  return {
    head: [['Categoría', 'N°', 'Criterio', 'Puntaje']],
    body: tableBody(groups),
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: [41, 98, 255] },
    columnStyles: { 0: { cellWidth: 16 }, 1: { cellWidth: 18 }, 2: { fontStyle: 'bold' }, 3: { cellWidth: 20, halign: 'right', fontStyle: 'bold' } },
    didParseCell: styleCells,
    didDrawCell: drawVertical,
  };
}

/** Escribe el encabezado con metadatos. */
function writeHeader(doc: any, meta: PdfMeta): void {
  doc.setFontSize(16);
  doc.text('Formulario de evaluación', 14, 15);
  doc.setFontSize(10);
  doc.text(`Módulo: ${meta.module}  Grupo: ${meta.group}  Año: ${meta.year}`, 14, 22);
}

/** Nombre del archivo PDF a descargar. */
function fileName(meta: PdfMeta): string {
  return `evaluacion-${meta.module}-${meta.year}.pdf`;
}

// TODO(pdf): restaurar import dinámico tras `pnpm add jspdf jspdf-autotable`.
// El dev-server de Angular pre-empaqueta los imports detectados y falla
// si la librería no está instalada, aunque lleven @vite-ignore.
/** Carga jspdf bajo demanda (stub hasta instalar la librería). */
async function loadJsPdf(): Promise<any> {
  throw new Error('Librería PDF no instalada. Ejecuta: pnpm add jspdf jspdf-autotable');
}

/** Carga el plugin de tablas bajo demanda (stub hasta instalarlo). */
async function loadAutoTable(): Promise<any> {
  throw new Error('Librería PDF no instalada. Ejecuta: pnpm add jspdf jspdf-autotable');
}

/** Escribe la tabla del formulario en el documento. */
async function writeBody(doc: any, groups: PdfGroup[]): Promise<void> {
  const autoTable = await loadAutoTable();
  autoTable(doc, tableConfig(groups));
}

/** Categorías del preview o lista vacía. */
export function previewCategories(preview: any): any[] {
  return preview?.categories || [];
}

/** Texto de error de generación PDF. */
export function pdfErrorText(err: any): string {
  return String(err?.message || err);
}

/** Genera y descarga el PDF del formulario completo. */
export async function downloadEvaluationPdf(meta: PdfMeta, categories: any[]): Promise<void> {
  const { jsPDF } = await loadJsPdf();
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  writeHeader(doc, meta);
  await writeBody(doc, buildPdfGroups(categories));
  doc.save(fileName(meta));
}
